import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import Notifications from 'react-notify-toast';
import styled from 'styled-components';

import { Breakpoints, Color } from '../../app/StyleGuide';
import alertService from '../../services/AlertService';
import LockedCourse from './LockedCourse';
import StepContent from './StepContent';
import TrainingCompleted from './TrainingCompleted';
import TrainingMenu from './TrainingMenu';

import { useParams } from 'react-router-dom';
import useQueryParams from '../../hooks/useQueryParams';
import workflowService from '../../services/workflowService';
import { useUser } from '../authentication';
import { getTasklistInstance } from './api/getTasklistByInstanceId';
import { type Course } from './models/course';
import { StepsProvider, toggleMobileTrainingMenu, useSteps } from './StepsContext';
import LoadingState from '../../components/LoadingState';
import { ErrorMessage } from '../../components/AlertMessages';
import { getUserProfileById } from './api/getUserProfileById';
import useModal from '../../hooks/useModal';
import { logout } from '../authentication';
import { ModalBody, ModalContainer, ModalFooter, ModalHeader } from '../../components/ModalElements';
import { MediumButton, MediumPrimaryButton } from '../../components/Buttons';
import windowService from '../../services/windowService';
import { useOrg } from '../org';

const fakeTraining = {
  name: 'Training Name',
  id: 'training-id-1',
  assignorId: {
    id: 'assignorId',
  },
  assigneeId: {
    id: 'assigneeId',
  },
  ownerName: 'Ministry Grid',
  description: 'Training description goes here',
  status: 'not_started',
  currentStep: {
    task: {
      assetUrl: 'asset-url',
      taskDescription: '<p>description p1</p><p>description p2</p>',
      linkUrl: 'google.com',
    },
  },
  tasks: [
    {
      id: {
        id: '123',
      },
      task: {
        _id: {
          id: '456',
        },
        taskName: 'Task 1',
        order: 1,
        response: '',
        _type: 'Text',
      },
      status: 'complete',
      assigneeId: {
        id: '789',
      },
      assignorId: {
        id: '101',
      },
      startDate: 1717000000000,
    },
  ],
};

const NotAssignedContainer = styled.div`
  filter: blur(10px);
`;

const StepsLayout = styled.div`
  display: flex;

  .mobile-menu-bg.visible {
    position: fixed;
    height: 100vh;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 250;
    @media screen and (min-width: ${Breakpoints.screen_md}) {
      display: none;
    }
  }
`;

export default function Steps() {
  const { user } = useUser();
  const { org } = useOrg();
  const { trainingId } = useParams<{ trainingId: string }>();
  const { stepId, isWorkflow } = useQueryParams({ preserveParams: true });
  const [training, setTraining] = useState<{ data?: Course; error: boolean; isLoading: boolean }>({
    isLoading: true,
    error: false,
  });
  const [assigneeUser, setAssigneeUser] = useState<{ email: string }>();

  useEffect(() => {
    getTraining();
  }, [trainingId, stepId, isWorkflow]);

  const getTraining = () => {
    setTraining({ isLoading: true, error: false });
    const trainingPromise = isWorkflow
      ? workflowService.getWorkflowInstanceDetailById(trainingId)
      : getTasklistInstance(trainingId);

    trainingPromise
      .then(result => {
        const training = result as Course & { assigneeId?: { id: string } };
        const assigneeId = training.assignedUser !== undefined ? training.assignedUser : training.assigneeId?.id;

        if (assigneeId && assigneeId !== user?.userId) {
          getUserProfileById(assigneeId).then(assignedUser => {
            setAssigneeUser({ email: assignedUser.email });
            openModal();
          });
        }
        setTraining({ data: training, error: false, isLoading: false });
      })
      .catch(error => {
        setTraining({ error: true, isLoading: false });
      });
  };

  const handleRedirectToLogin = () => {
    logout(window.location.hash);
  };

  const [modal, openModal, dismissModal] = useModal((type: string, payload: string, dismissModal: () => void) => {
    return (
      <ModalContainer dismissHandler={dismissModal}>
        <ModalHeader>
          <h3 data-qa-hook="modalHeader">Training Access Error</h3>
        </ModalHeader>
        <ModalBody data-qa-hook="modalMessage">
          <h4>
            You are not the assignee of this training. Would you like to login to the correct account to access this
            training?
          </h4>
        </ModalBody>
        <ModalFooter>
          <MediumButton onClick={() => windowService.redirectTo('/dashboard')}>No, Go To My Dashboard</MediumButton>
          <MediumPrimaryButton onClick={handleRedirectToLogin}>Yes, Login</MediumPrimaryButton>
        </ModalFooter>
      </ModalContainer>
    );
  });

  const userWithOrg = {
    ...user,
    isOrgUser: !!org,
    currentOrgName: org?.name ?? '',
    isCommentModerator: user?.permissions?.includes('Comment Moderator') ?? false,
  };

  if (assigneeUser)
    return (
      <NotAssignedContainer>
        <StepsProvider trainingInput={fakeTraining} stepInstanceId={''} user={userWithOrg}>
          <CurrentSteps />
        </StepsProvider>
        {modal}
      </NotAssignedContainer>
    );
  if (training.isLoading && !training.data) return <LoadingState />;
  if (training.error) return <ErrorMessage message="An error occurred while loading the training." />;

  return (
    <StepsProvider trainingInput={training.data!} stepInstanceId={stepId} user={userWithOrg}>
      <CurrentSteps />
    </StepsProvider>
  );
}

const CurrentSteps = () => {
  const [{ isMobileTrainingMenuOpen, currentCourse, showTrainingCompletedModal }, dispatch] = useSteps();

  useEffect(() => {
    setTimeout(() => toggleMobileTrainingMenu(dispatch, true), 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (localStorage.getItem('showBanner')) {
      const text = localStorage.getItem('showBanner');
      alertService.show(text);
      localStorage.removeItem('showBanner');
    }
    return () => {
      if (localStorage.getItem('routeBeforeSteps')) localStorage.removeItem('routeBeforeSteps');
    };
  }, []);

  const purchaseRequired = !!currentCourse.purchaseRequired;

  const stepsClass = classNames({
    'no-scroll': isMobileTrainingMenuOpen,
  });

  const mobileMenuClass = classNames({
    'mobile-menu-bg': true,
    visible: isMobileTrainingMenuOpen,
  });

  return (
    <>
      <Notifications />
      <StepsLayout className={stepsClass}>
        {purchaseRequired ? <LockedCourse /> : <StepContent />}
        <TrainingMenu close={() => toggleMobileTrainingMenu(dispatch, false)} />
        <div onClick={() => toggleMobileTrainingMenu(dispatch, false)} className={mobileMenuClass}></div>
        {showTrainingCompletedModal && <TrainingCompleted />}
      </StepsLayout>
    </>
  );
};
